import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Lock, Mail, Shield, User, Eye, EyeOff, KeyRound, CheckCircle2 } from "lucide-react";

export default function LoginPage({ userType: forcedType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, API } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotIdent, setForgotIdent] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const userType = forcedType || "contribuyente";

  const isFuncionario = userType === "funcionario";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email.trim(), password.trim(), userType);
    setLoading(false);
    if (r.ok) {
      navigate(r.user.role === "contribuyente" ? "/portal" : "/dashboard");
    }
  };

  const openForgot = () => {
    setForgotIdent(email || "");
    setForgotDone(false);
    setForgotOpen(true);
  };

  const submitForgot = async (ev) => {
    ev.preventDefault();
    if (!forgotIdent.trim()) { toast.error("Ingrese su correo o RIF"); return; }
    setForgotBusy(true);
    try {
      const r = await axios.post(`${API}/auth/public/request-password-reset`, { identificador: forgotIdent.trim() });
      toast.success(r.data.message || "Solicitud enviada");
      setForgotDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar la solicitud");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="login-page">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium" data-testid="login-back-link">
        <ArrowLeft className="w-4 h-4" /> Volver al inicio
      </Link>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          <div className="flex flex-col items-center mb-7">
            <div className={`w-16 h-16 rounded-2xl ${isFuncionario ? "bg-orange-500" : "bg-green-700"} flex items-center justify-center shadow-lg mb-4`}>
              {isFuncionario ? <Shield className="w-8 h-8 text-white" /> : <User className="w-8 h-8 text-white" />}
            </div>
            <h1 className="font-display font-extrabold text-2xl text-green-900">
              {isFuncionario ? "Portal Funcionario" : "Portal del Contribuyente"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isFuncionario ? "Acceso al panel administrativo" : "Ingrese para consultar y pagar"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-green-900 font-semibold">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-700" />
                <Input id="email" data-testid="login-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required placeholder="correo@dominio.com"
                  className="pl-10 h-11 rounded-xl border-green-200 focus:border-green-600 focus:ring-green-600/30" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-green-900 font-semibold">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-700" />
                <Input id="password" data-testid="login-password-input" type={showPass ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                  className="pl-10 pr-10 h-11 rounded-xl border-green-200 focus:border-green-600 focus:ring-green-600/30" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700" data-testid="login-toggle-password">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className={`w-full h-11 rounded-xl font-semibold text-white text-base shadow-lg ${isFuncionario ? "bg-orange-500 hover:bg-orange-600" : "bg-green-700 hover:bg-green-800"}`}>
              {loading ? "Verificando..." : "Iniciar sesión"}
            </Button>

            {!isFuncionario && (
              <div className="text-center">
                <button type="button" onClick={openForgot} data-testid="login-forgot-link"
                  className="text-sm text-green-700 hover:text-orange-600 font-semibold inline-flex items-center gap-1">
                  <KeyRound className="w-4 h-4" /> ¿Olvidaste tu contraseña?
                </button>
              </div>
            )}
          </form>

          {!isFuncionario && (
            <p className="mt-5 text-center text-sm text-slate-500">
              ¿Primera vez aquí?{" "}
              <Link to="/registro" className="text-orange-600 hover:text-orange-700 font-semibold" data-testid="login-register-link">
                Crear cuenta
              </Link>
            </p>
          )}
          <div className="mt-4 text-center text-xs">
            <Link to={isFuncionario ? "/login/contribuyente" : "/login/funcionario"} className="text-green-700 hover:text-green-900 font-medium" data-testid="login-switch-type">
              {isFuncionario ? "Soy contribuyente" : "Soy funcionario"}
            </Link>
          </div>
        </div>
      </div>

      {/* Diálogo: ¿Olvidaste tu contraseña? */}
      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) { setForgotDone(false); setForgotIdent(""); } }}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-900">
              <KeyRound className="w-5 h-5 text-orange-500" /> Recuperar contraseña
            </DialogTitle>
            <DialogDescription>
              {forgotDone
                ? "Revise su bandeja de entrada y la carpeta de spam."
                : "Indique el correo o RIF con el que se registró. Le enviaremos un enlace para crear una nueva contraseña. El enlace es válido por 24 horas."}
            </DialogDescription>
          </DialogHeader>

          {forgotDone ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
              <p className="mt-3 text-green-800 font-semibold">Solicitud recibida</p>
              <p className="mt-2 text-sm text-slate-600">
                Si el dato ingresado corresponde a una cuenta activa del portal, recibirá un enlace de restablecimiento en breve.
              </p>
              <Button data-testid="forgot-close-btn" className="mt-6 w-full bg-green-700 hover:bg-green-800 text-white" onClick={() => setForgotOpen(false)}>
                Cerrar
              </Button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-2">
                <Label className="text-green-900 font-semibold">Correo o RIF</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-700" />
                  <Input data-testid="forgot-ident-input" value={forgotIdent} onChange={(e) => setForgotIdent(e.target.value)}
                    required placeholder="correo@dominio.com  ó  J-12345678-9"
                    className="pl-10 h-11 rounded-xl border-green-200 focus:border-green-600" />
                </div>
                <p className="text-xs text-slate-500">
                  Puede usar el correo con el que se registró o el RIF/documento del contribuyente.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={forgotBusy} data-testid="forgot-submit-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
                  <KeyRound className="w-4 h-4 mr-1.5" /> {forgotBusy ? "Enviando…" : "Enviarme el enlace"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
